import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Clock, MapPin, Check, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Kiosk() {
  const [pin, setPin] = useState('');
  const [location, setLocation] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showSuccess, setShowSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [kioskStep, setKioskStep] = useState('pin'); // 'pin' | 'comment'
  const [kioskData, setKioskData] = useState(null); // { token, user, is_clocked_in, active_entry }
  const [kioskComment, setKioskComment] = useState('');
  const [kioskBusy, setKioskBusy] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const startCamera = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError('Myndavél ekki studd í þessum vafra');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraStream(stream);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraError(null);
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError('Gat ekki opnað myndavél. Stimplun virkar samt án myndar.');
    }
  }, []);

  const getLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => console.error('Location error:', error)
      );
    }
  }, []);

 

  useEffect(() => {
    getLocation();

    // Reyna að ræsa myndavél strax (fyrir iPad kiosk)
    startCamera();
    
    // Update clock every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      clearInterval(timer);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [getLocation, startCamera]);

  // Endurtengja myndavélarstraum við video element hvenær sem hann remountast
  useEffect(() => {
    if (cameraStream && videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = cameraStream;
    }
  });

 

  const handlePinInput = (digit) => {
    if (pin.length < 4) {
      setPin(pin + digit);
    }
  };

  const handleClear = () => {
    setPin('');
    setShowSuccess(false);
    setSuccessData(null);
  };

  const capturePhotoBase64 = () => {
    if (!videoRef.current || !canvasRef.current || !cameraStream) return null;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;

    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, width, height);

    // Fá base64 JPG gögn
    return canvas.toDataURL('image/jpeg', 0.8);
  };

  const handleSubmit = async () => {
    if (pin.length !== 4) {
      toast.error('PIN verður að vera 4 tölustafir');
      return;
    }

    try {
      const loginRes = await axios.post(`${API}/kiosk/login`, { pin });
      setKioskData(loginRes.data);
      setKioskComment('');
      setKioskStep('comment');
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Rangt PIN númer');
      setPin('');
    }
  };

  const handleConfirmClock = async () => {
    if (kioskBusy || !kioskData) return;
    setKioskBusy(true);
    const { token, user, is_clocked_in, active_entry } = kioskData;

    try {
      if (is_clocked_in) {
        const photo_base64 = capturePhotoBase64();
        await axios.post(
          `${API}/time/clock-out`,
          { entry_id: active_entry.id, comment: kioskComment || null, photo_base64 },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const clockInTime = new Date(active_entry.clock_in);
        const clockOutTime = new Date();
        const hours = ((clockOutTime - clockInTime) / (1000 * 60 * 60)).toFixed(1);
        setSuccessData({ name: user.full_name, action: 'út', time: formatTime(clockOutTime), hours });
      } else {
        const photo_base64 = capturePhotoBase64();
        await axios.post(
          `${API}/time/clock-in`,
          { location_lat: location?.lat, location_lng: location?.lng, photo_base64, comment: kioskComment || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccessData({ name: user.full_name, action: 'inn', time: formatTime(new Date()) });
      }

      setShowSuccess(true);
      setPin('');
      setKioskStep('pin');
      setKioskData(null);

      setTimeout(() => {
        setShowSuccess(false);
        setSuccessData(null);
      }, 3000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Villa kom upp');
    } finally {
      setKioskBusy(false);
    }
  };

  const handleCancelComment = () => {
    setKioskStep('pin');
    setKioskData(null);
    setKioskComment('');
    setPin('');
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('is-IS', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('is-IS', { 
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (showSuccess && successData) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/95 backdrop-blur-md" data-testid="success-screen">
        {/* Halda myndavél í gangi á meðan success skjár sýnist */}
        <video ref={videoRef} autoPlay playsInline muted style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} />
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        <div className="text-center space-y-8 px-6">
          <div className="w-28 h-28 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
            <Check className="w-14 h-14 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-4xl font-bold text-slate-100 mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              {successData.name}
            </h2>
            <p className="text-2xl text-slate-300 mb-2">
              Stimpluð/ur <span className={successData.action === 'inn' ? 'text-emerald-400 font-bold' : 'text-amber-400 font-bold'}>{successData.action}</span>
            </p>
            <p className="text-5xl font-bold text-slate-100" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {successData.time}
            </p>
            {successData.hours && (
              <p className="text-xl text-slate-400 mt-4">
                Unnið: <strong className="text-slate-200">{successData.hours}h</strong>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-900" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Status bar */}
      {location && (
        <div className="flex items-center justify-center gap-2 py-2 bg-slate-800/80 border-b border-slate-700/50">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-slate-400 text-xs font-medium tracking-wide uppercase">Staðsetning virk</span>
        </div>
      )}

      {/* Clock display */}
      <div className="flex flex-col items-center justify-center pt-10 pb-6 px-6">
        <div className="text-7xl font-bold text-slate-100 tracking-tight" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          {currentTime.toLocaleTimeString('is-IS', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
        </div>
        <div className="text-base text-slate-400 mt-2 capitalize">
          {formatDate(currentTime)}
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex items-start justify-center px-4 pb-6" data-testid="kiosk-pin-mode">
        {/* Hidden camera elements */}
        <video ref={videoRef} autoPlay playsInline muted style={{ display: 'none' }} />
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {/* PIN step */}
        {kioskStep === 'pin' && (
          <div className="w-full max-w-sm space-y-6">
            <p className="text-center text-slate-400 text-sm font-medium tracking-wider uppercase">
              Sláðu inn PIN
            </p>

            {/* PIN dots */}
            <div className="flex justify-center gap-5 mb-4">
              {[0, 1, 2, 3].map((index) => (
                <div
                  key={index}
                  className={`w-5 h-5 rounded-full border-2 transition-all duration-200 ${
                    pin.length > index
                      ? 'bg-blue-500 border-blue-500 shadow-[0_0_12px_rgba(59,130,246,0.5)]'
                      : 'bg-transparent border-slate-600'
                  }`}
                  data-testid={`pin-dot-${index}`}
                />
              ))}
            </div>

            {/* Number pad */}
            <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => handlePinInput(num.toString())}
                  className="h-16 w-16 mx-auto rounded-2xl bg-slate-800 border border-slate-700 text-slate-100 text-2xl font-bold transition-all active:scale-95 hover:border-blue-500/50 hover:shadow-[0_0_15px_rgba(59,130,246,0.15)] flex items-center justify-center"
                  data-testid={`pin-button-${num}`}
                >
                  {num}
                </button>
              ))}
              <div />
              <button
                type="button"
                onClick={() => handlePinInput('0')}
                className="h-16 w-16 mx-auto rounded-2xl bg-slate-800 border border-slate-700 text-slate-100 text-2xl font-bold transition-all active:scale-95 hover:border-blue-500/50 flex items-center justify-center"
                data-testid="pin-button-0"
              >
                0
              </button>
              <button
                type="button"
                onClick={handleClear}
                className="h-16 w-16 mx-auto rounded-2xl bg-slate-800/50 border border-red-900/40 text-red-400 transition-all active:scale-95 hover:bg-red-900/20 flex items-center justify-center"
                data-testid="pin-clear-button"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={pin.length !== 4}
              className="w-full h-14 rounded-2xl bg-blue-600 hover:bg-blue-500 disabled:bg-slate-800 disabled:text-slate-600 disabled:border-slate-700 text-white text-lg font-bold transition-all active:scale-[0.98] border border-blue-500 disabled:border-slate-700 flex items-center justify-center gap-2"
              data-testid="pin-submit-button"
            >
              <Check className="w-5 h-5" />
              Stimpla
            </button>
          </div>
        )}

        {/* Comment step */}
        {kioskStep === 'comment' && kioskData && (
          <div className="w-full max-w-sm space-y-5 bg-slate-800 rounded-3xl border border-slate-700 p-6" data-testid="kiosk-comment-card">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-slate-100" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                {kioskData.user.full_name}
              </h2>
              <p className={`text-lg font-semibold mt-1 ${kioskData.is_clocked_in ? 'text-amber-400' : 'text-emerald-400'}`}>
                {kioskData.is_clocked_in ? 'Stimpla út' : 'Stimpla inn'}
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-400">Athugasemd (valfrjálst)</label>
              <textarea
                value={kioskComment}
                onChange={(e) => setKioskComment(e.target.value)}
                placeholder="T.d. fékk leyfi frá yfirmanni..."
                className="w-full bg-slate-900 border border-slate-600 rounded-xl p-3 text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none h-24 text-base"
                data-testid="kiosk-comment-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleCancelComment}
                className="h-14 rounded-xl bg-slate-700 hover:bg-slate-600 text-slate-200 font-bold text-base transition-all active:scale-95"
                data-testid="kiosk-cancel-button"
              >
                Hætta við
              </button>
              <button
                onClick={handleConfirmClock}
                disabled={kioskBusy}
                className={`h-14 rounded-xl font-bold text-base text-white transition-all active:scale-95 flex items-center justify-center gap-2 ${
                  kioskData.is_clocked_in
                    ? 'bg-amber-600 hover:bg-amber-500'
                    : 'bg-emerald-600 hover:bg-emerald-500'
                }`}
                data-testid="kiosk-confirm-button"
              >
                <Check className="w-5 h-5" />
                {kioskBusy ? '...' : kioskData.is_clocked_in ? 'Stimpla út' : 'Stimpla inn'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Admin link */}
      <div className="py-4 text-center">
        <a
          href="/login"
          className="text-xs text-slate-600 hover:text-slate-400 transition-colors"
          data-testid="admin-login-link"
        >
          Stjórnendaaðgangur
        </a>
      </div>
    </div>
  );
}